import * as React from "react";
import { History } from "history";
import { Modal } from "react-bootstrap";
import * as XLSX from "xlsx";

import { shiftNumber, getURLHashParamsMap, updateURLHash, stringCompare, fromShiftTag } from "@/lib/util";
import { translate, IntlProviderWrapper, getLang } from "@/modules/shared/IntlProviderWrapper";
import {
    storeInsightsSelectionLineUUIDs,
    storeInsightsSelectionEventTypes,
    storeInsightsShowCheckboxesOption,
    setInsightSelectionLineUUIDsWIP,
    getInsightsSelectionWIP,
    getDefaultInsightTypesForUser,
    exportInsightsHTML,
} from "@/lib/insights";
import type { InsightAction } from "@/lib/models";
import type { ShiftNumber } from "@/lib/models";
import { getBackend as getBackend2 } from "@/lib/backend/backend2";
import * as t from "@/lib/backend/manufacturing2.generated.types";
import {
    filterLinesForUser,
    getLineGroupsForUser,
    getPlantsForUser,
    waitForManufacturingAsync,
} from "@/lib/businessLogic";
import local_settings, { FIELD_INSIGHTS_PRINT_NUMBER_OF_DAYS } from "@/lib/localSettings";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";

import { FormattedMessage } from "react-intl";
import Checkbox from "@/modules/shared/ui/Checkbox";
import Insights from "./Insights";
import Loader from "@/modules/shared/ui/Loader";
import { renderToString } from "react-dom/server";
import { INSIGHT_TAGS_ACCESS, INSIGHT_TYPES } from "@/lib/ManufacturingTags.generated";

import "./Insights.scss";

const getNumberOfDays = (): number => {
    let num_days = Number(local_settings.getItem(FIELD_INSIGHTS_PRINT_NUMBER_OF_DAYS));
    return Number.isNaN(num_days) ? 0 : num_days;
};

const storeNumberOfDays = (num_days: number) => {
    local_settings.setItem(FIELD_INSIGHTS_PRINT_NUMBER_OF_DAYS, `${num_days}`);
};

type PrintModalProps = {
    show: boolean;
    onPrint: (state: PrintModalState) => void;
    onHide: () => void;
};

type PrintModalState = {
    num_days: number;
};

class PrintModal extends React.Component<PrintModalProps, PrintModalState> {
    state: PrintModalState = {
        num_days: getNumberOfDays(),
    };

    handleDaysChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const target = event.target;
        this.setState({
            num_days: Number(target.value),
        });
    };

    handleDaysFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        const target = event.target;
        target.select();
    };

    handlePrintClick = () => {
        storeNumberOfDays(this.state.num_days);
        this.props.onPrint(this.state);
    };

    render() {
        return (
            <Modal size="sm" show={this.props.show} onHide={this.props.onHide}>
                <Modal.Header>
                    <Modal.Title>{translate("common.print", "Print")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form>
                        <div className="form-group mb-0">
                            <label htmlFor="number-of-days-input">
                                {translate("common.number_of_days", "Number of days")} (0 ={" "}
                                {translate("common.print_all", "Print all")})
                            </label>
                            <input
                                id="number-of-days-input"
                                className="form-control"
                                type="number"
                                value={`${this.state.num_days}`.replace(/^0+(.+)/, "$1")}
                                onChange={this.handleDaysChange}
                                onFocus={this.handleDaysFocus}
                                min={0}
                            />
                        </div>
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <button onClick={this.props.onHide} className="btn btn-outline-primary">
                        {translate("common.cancel", "Cancel")}
                    </button>
                    <button onClick={this.handlePrintClick} className="btn btn-outline-primary">
                        {translate("common.print", "Print")}
                    </button>
                </Modal.Footer>
            </Modal>
        );
    }
}

type Props = {
    history: History;
    insights: t.IEventDataEx[];
    reportid: string;
    year?: number;
    week?: number;
    show_filter: boolean;
    show_only_future: boolean;
    show_links: boolean;
    show_actions: boolean;
    reverse_order?: boolean;
    allowed_actions?: InsightAction[];
    onActionSelect?: (action: t.IInsightAction, insight: t.IEventDataEx) => void;
};

type IUuidSelected = Record<string, boolean>;

type ILocalInsightType = {
    type: string;
    name: string;
};

type ILocalLine = {
    uuid: string;
    title: string;
    plant_uuid: string;
};

type State = {
    // if only future insights should be displayed in insights table
    insight_types: ILocalInsightType[];
    lines: ILocalLine[];
    line_groups: t.ILineGroupData[];
    plants: t.IPlantData[];
    selected_insight_types: IUuidSelected;
    selected_line_uuids: IUuidSelected;
    selected_plant_uuids: IUuidSelected;
    selected_line_group_uuids: IUuidSelected;
    insights_show_checkboxes: boolean;
    loading_actions: boolean;
    actions_error: boolean;
    allowed_plants: string[];
    allowed_line_groups: string[];
    loading: boolean;
    show_print_modal: boolean;
};

export const prepareInsightsExportTable = (insights: t.IEventDataEx[]): (string | number | Date)[][] => {
    const lang = getLang();
    const columns: string[] = [
        translate("common.week", "Week"),
        translate("common.date", "Date"),
        translate("common.shift", "Shift"),
        translate("OrderTableProduction.line_external_id", "Line ID"),
        translate("common.line", "Line"),
        translate("OrderTableProduction.material_external_id", "Material ID"),
        translate("common.material", "Material"),
        translate("common.order", "Order"),
        translate("common.quantity", "Quantity"),
        translate("common.duration", "Duration"),
    ];
    const column_data_counters: Map<string, number> = new Map(columns.map(column => [column, 0]));
    const rows: { [key: string]: string | number | Date }[] = [];
    for (const insight of insights) {
        const extra_data =
            typeof insight.extra_data === "object" && insight.extra_data !== null ? insight.extra_data : {};
        const tags = insight.tags;
        const shift_tag = INSIGHT_TAGS_ACCESS.shift_tag(tags);
        const shift_date = fromShiftTag(shift_tag);
        const shift_number = shiftNumber(shift_date);

        const row: { [key: string]: string | number | Date } = {};
        for (const column of columns) {
            let inc = true;
            switch (column) {
                case translate("common.week", "Week"):
                    row[column] = shift_number.week;
                    break;
                case translate("common.date", "Date"):
                    row[column] = new Date(insight.ts);
                    break;
                case translate("common.shift", "Shift"):
                    row[column] = shift_number.shift_day + 1;
                    break;
                case translate("OrderTableProduction.line_external_id", "Line ID"):
                    row[column] = tags.line_external_id || extra_data.line_external_id || "";
                    inc = row[column] !== "";
                    break;
                case translate("common.line", "Line"):
                    row[column] = tags.line_title || extra_data.line_title || "";
                    inc = row[column] !== "";
                    break;
                case translate("OrderTableProduction.material_external_id", "Material ID"):
                    row[column] = extra_data.material || "";
                    break;
                case translate("common.material", "Material"):
                    row[column] =
                        extra_data.material_title ||
                        (extra_data.material_title_translated !== undefined &&
                            extra_data.material_title_translated[lang] !== undefined)
                            ? extra_data.material_title_translated[lang]
                            : "";
                    inc = row[column] !== "";
                    break;
                case translate("common.order", "Order"):
                    row[column] = INSIGHT_TAGS_ACCESS.order_external_id(tags) || extra_data.order_external_id || "";
                    inc = row[column] !== "";
                    break;
                case translate("common.quantity", "Quantity"):
                    row[column] = extra_data.quantity !== undefined ? extra_data.quantity : -1;
                    inc = row[column] !== -1;
                    break;
                case translate("common.duration", "Duration"):
                    row[column] =
                        extra_data.setup_execution_time !== undefined
                            ? Number.parseFloat(extra_data.setup_execution_time)
                            : -1;
                    inc = row[column] !== -1;
                    break;
                default:
                    break;
            }

            if (inc) {
                column_data_counters.set(column, (column_data_counters.get(column) || 0) + 1);
            }
        }

        rows.push(row);
    }

    const data: (string | number | Date)[][] = [];
    const header: (string | number | Date)[] = [];
    for (const column of columns) {
        if ((column_data_counters.get(column) || 0) > 0) {
            header.push(column);
        }
    }

    if (header.length > 0) {
        data.push(header);
        for (const row of rows) {
            const data_row: (string | number | Date)[] = [];
            for (const column of columns) {
                if ((column_data_counters.get(column) || 0) > 0) {
                    data_row.push(row[column]);
                }
            }

            data.push(data_row);
        }
    }

    return data;
};

// Displays the list of insights
class InsightsList extends React.Component<Props, State> {
    nodeRef = React.createRef<HTMLDivElement>();

    state: State = {
        insight_types: [],
        plants: [],
        line_groups: [],
        lines: [],
        selected_insight_types: {},
        selected_line_uuids: {},
        selected_plant_uuids: {},
        selected_line_group_uuids: {},
        insights_show_checkboxes: false,
        loading_actions: false,
        actions_error: false,
        allowed_plants: [],
        allowed_line_groups: [],
        loading: true,
        show_print_modal: false,
    };

    arrayToSelectedCheckboxesMap(array: string[]) {
        if (array) {
            return array.reduce((map, key) => {
                map[key] = true;
                return map;
            }, {});
        }
        return {};
    }

    getInsightTypeToURLMappings(insight_types: string[]) {
        return insight_types.reduce((hashmap, item) => {
            const urlString = item.replace(/_/g, "-").replace(/\./g, "-");
            hashmap[item] = urlString;
            return hashmap;
        }, {});
    }

    getInsightTypeFromURLMappings(insight_types: ILocalInsightType[]) {
        return insight_types.reduce((hashmap, item) => {
            const urlString = item.type.replace(/_/g, "-").replace(/\./g, "-");
            hashmap[urlString] = item.type;
            return hashmap;
        }, {});
    }

    updateURLHash() {
        if (!getInsightsSelectionWIP()) {
            let lines_selected: IUuidSelected = {}; // getInsightSelectionLineUUIDs();
            let insights_selected: IUuidSelected = {}; // getInsightSelectionEventTypes();
            let lines: string[] = [];
            let insights: string[] = [];
            const urlHashParams: Record<string, string[]> = {};
            if (lines_selected) {
                lines = Object.keys(lines_selected)
                    .filter(key => lines_selected[key])
                    .sort();
            }
            if (insights_selected) {
                insights = Object.keys(insights_selected)
                    .filter(key => insights_selected[key])
                    .sort();
                const insightsURLMapping = this.getInsightTypeToURLMappings(insights);
                insights = insights.map(item => insightsURLMapping[item]);
            }
            urlHashParams.line_uuids = lines;
            urlHashParams.insight_types = insights;

            updateURLHash(urlHashParams);
        }
    }

    componentWillUnmount() {
        const el = this.nodeRef.current;

        if (el) {
            el.removeEventListener("export-html-insights", this.handleExportHTML);
            el.removeEventListener("export-xlsx-insights", this.handleExportXlsx);
        }
    }

    async componentDidMount() {
        const el = this.nodeRef.current;

        if (el) {
            el.addEventListener("export-html-insights", this.handleExportHTML);
            el.addEventListener("export-xlsx-insights", this.handleExportXlsx);
        }

        try {
            await waitForManufacturingAsync();
            // get list of all lines

            setInsightSelectionLineUUIDsWIP(true);

            // select all plants that user has access to
            const selected_plant_uuids: IUuidSelected = {};
            let plants = getPlantsForUser();
            plants.forEach(plant => (selected_plant_uuids[plant.uuid] = true));

            // select all linegroup that user has access to
            const linegroups_selected: IUuidSelected = {};
            const line_groups = getLineGroupsForUser();
            line_groups.forEach(x => {
                linegroups_selected[x.uuid] = true;
            });

            let selected_line_uuids: IUuidSelected = {};
            const res_lines = await getBackend2().manufacturing.getLines({});
            const lines = filterLinesForUser(res_lines.lines).map(line => ({
                uuid: line.uuid,
                title: line.title,
                plant_uuid: line.plant_uuid,
            }));
            lines.forEach(line => {
                selected_line_uuids[line.uuid] = true;
            });

            this.setState({
                lines,
                selected_line_uuids,
                selected_plant_uuids,
                plants,
                line_groups,
                selected_line_group_uuids: linegroups_selected,
            });
            this.updateURLHash();

            const res2 = await getBackend2().manufacturing.getInsightTypes({});

            const insight_types: ILocalInsightType[] = res2.insight_types
                .filter(insight => !!insight.code)
                .map(insight => ({ type: insight.code, name: insight.title }))
                .sort((a, b) => stringCompare(a.name, b.name));

            let selected_insight_types: any = {};
            if (this.props.show_filter) {
                const hashparams = getURLHashParamsMap();
                const paramsFromURL = Object.keys(hashparams).length > 0;
                if (paramsFromURL) {
                    selected_insight_types = this.arrayToSelectedCheckboxesMap(hashparams.insight_types);
                    const insightsURLMapping = this.getInsightTypeFromURLMappings(insight_types);
                    selected_insight_types = Object.keys(selected_insight_types).reduce((hashmap, item) => {
                        hashmap[insightsURLMapping[item]] = true;
                        return hashmap;
                    }, {});
                }
                const default_insights = getDefaultInsightTypesForUser();
                insight_types.forEach(insight => {
                    selected_insight_types[insight.type] = default_insights.indexOf(insight.type) >= 0;
                });
            } else {
                // just turn everything on
                insight_types.forEach(insight => {
                    selected_insight_types[insight.type] = true;
                });
            }
            this.setState({
                insight_types,
                selected_insight_types,
                loading: false,
            });
            this.updateURLHash();
        } catch (error) {
            console.log(error);
        }
    }

    handlePlantChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const target = event.target;
        let { name } = target;
        name = name.replace("plant-checkbox-", "");
        this.setState(
            (prev_state: State) => {
                const selected_plant_uuids = {
                    ...prev_state.selected_plant_uuids,
                    [name]: !prev_state.selected_plant_uuids[name],
                };
                prev_state.selected_plant_uuids = selected_plant_uuids;
                return prev_state;
            },
            () => {
                this.calculateLineFilters();
            }
        );
    };

    handleLinegroupChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const target = event.target;
        let { name } = target;
        name = name.replace("linegroup-checkbox-", "");
        this.setState(
            (prev_state: State) => {
                const new_value = !prev_state.selected_line_group_uuids[name];
                const selected_line_group_uuids = {
                    ...prev_state.selected_line_group_uuids,
                    [name]: new_value,
                };
                prev_state.selected_line_group_uuids = selected_line_group_uuids;
                this.state.line_groups.forEach(lg => {
                    if (lg.uuid === name) {
                        // enable/disable all lines that belong to this line_group
                        lg.line_uuids.forEach(line_uuid => {
                            prev_state.selected_line_uuids[line_uuid] = new_value;
                        });
                    }
                });
                return prev_state;
            },
            () => {
                this.calculateLineFilters();
            }
        );
    };

    calculateLineFilters = () => {
        this.setState((prev_state: State) => {
            // first select all lines
            Object.keys(prev_state.selected_line_uuids).forEach(uuid => {
                prev_state.selected_line_uuids[uuid] = true;
            });

            // new deselect lines for unselected line-groups and plants
            prev_state.line_groups.forEach(lg => {
                const new_value = prev_state.selected_line_group_uuids[lg.uuid];
                if (!new_value) {
                    // disable all lines that belong to this line_group
                    lg.line_uuids.forEach(line_uuid => {
                        prev_state.selected_line_uuids[line_uuid] = false;
                    });
                }
            });
            prev_state.plants.forEach(plant => {
                const new_value = prev_state.selected_plant_uuids[plant.uuid];
                if (!new_value) {
                    // disable all lines that belong to this plant
                    prev_state.lines
                        .filter(line => line.plant_uuid === plant.uuid)
                        .forEach(line => {
                            prev_state.selected_line_uuids[line.uuid] = false;
                        });
                }
            });
            return prev_state;
        });
    };

    handleLineChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const target = event.target;
        let { name } = target;
        name = name.replace("line-checkbox-", "");
        this.setState((prev_state: State) => {
            const new_value = !prev_state.selected_line_uuids[name];
            const selected_line_uuids = {
                ...prev_state.selected_line_uuids,
                [name]: new_value,
            };
            prev_state.selected_line_uuids = selected_line_uuids;
            this.updateURLHash();
            return prev_state;
        });
    };

    handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const target = event.target;
        let { name } = target;
        name = name.replace("type-checkbox-", "");
        this.setState((prev_state: State) => {
            const selected_insight_types = {
                ...prev_state.selected_insight_types,
                [name]: !prev_state.selected_insight_types[name],
            };
            prev_state.selected_insight_types = selected_insight_types;
            this.updateURLHash();
            return prev_state;
        });
    };

    setAllPlantsFilter = (value: boolean) => {
        this.setState(
            (prev_state: State) => {
                for (const field of Object.keys(prev_state.selected_plant_uuids)) {
                    prev_state.selected_plant_uuids[field] = value;
                }
                return prev_state;
            },
            () => {
                this.calculateLineFilters();
            }
        );
    };

    setAllLinegroupsFilter = (value: boolean) => {
        this.setState(
            (prev_state: State) => {
                for (const field of Object.keys(prev_state.selected_line_group_uuids)) {
                    prev_state.selected_line_group_uuids[field] = value;
                }
                return prev_state;
            },
            () => {
                this.calculateLineFilters();
            }
        );
    };

    setAllLinesFilter = (value: boolean) => {
        this.setState((prev_state: State) => {
            for (const field of Object.keys(prev_state.selected_line_uuids)) {
                prev_state.selected_line_uuids[field] = value;
                storeInsightsSelectionLineUUIDs(prev_state.selected_line_uuids);
            }
            this.updateURLHash();
            return prev_state;
        });
    };

    setAllTypesFilter = (value: boolean) => {
        this.setState((prev_state: State) => {
            for (const field of Object.keys(prev_state.selected_insight_types)) {
                prev_state.selected_insight_types[field] = value;
                storeInsightsSelectionEventTypes(prev_state.selected_insight_types);
            }
            this.updateURLHash();
            return prev_state;
        });
    };

    toggleFilters = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        this.setState((prev_state: State) => {
            prev_state.insights_show_checkboxes = !prev_state.insights_show_checkboxes;
            storeInsightsShowCheckboxesOption(prev_state.insights_show_checkboxes);
            return prev_state;
        });
    };

    highlightCurrShift(shift_obj: ShiftNumber) {
        const curr_shift_obj = shiftNumber(new Date());
        if (
            shift_obj.shift === curr_shift_obj.shift &&
            shift_obj.week === curr_shift_obj.week &&
            shift_obj.year === curr_shift_obj.year
        ) {
            return "current-shift";
        } else {
            return "";
        }
    }

    renderPlantCheckboxes(plants: any) {
        const checkboxes = plants.map(plant => (
            <div className="col-sm-4 col-lg-4 col-xl-3" key={"plant-checkbox-w-" + plant.uuid}>
                <Checkbox
                    label={plant.title}
                    isSelected={this.state.selected_plant_uuids[plant.uuid]}
                    onCheckboxChange={this.handlePlantChange}
                    name={"plant-checkbox-" + plant.uuid}
                    key={"plant-checkbox-" + plant.uuid}
                />
            </div>
        ));
        if (this.state.insights_show_checkboxes) {
            const totalCounter = Object.keys(this.state.selected_plant_uuids).length;
            const selectedCounter = this.countSelectedCheckboxes(this.state.selected_plant_uuids);
            const selectAllActive = totalCounter && selectedCounter === totalCounter;
            const deselectAllActive = totalCounter && selectedCounter === 0;
            return (
                <div className="filter-group">
                    <div className="filter-group-header">
                        <h6>
                            <FormattedMessage id="common.plants" defaultMessage="Plants" />
                        </h6>
                        <div className="filter-group-actions ml-auto">
                            <a
                                className={selectAllActive ? "is-active" : undefined}
                                onClick={() => {
                                    this.setAllPlantsFilter(true);
                                }}
                            >
                                <FormattedMessage id="common.select_all" defaultMessage="Select all" />
                            </a>
                            <a
                                className={deselectAllActive ? "is-active" : undefined}
                                onClick={() => {
                                    this.setAllPlantsFilter(false);
                                }}
                            >
                                <FormattedMessage id="common.deselect_all" defaultMessage="Deselect all" />
                            </a>
                        </div>
                    </div>
                    <div className="row">{checkboxes}</div>
                </div>
            );
        }
    }

    renderLinegroupCheckboxes(line_groups: t.ILineGroupData[]) {
        const checkboxes = line_groups.map(line_group => (
            <div className="col-sm-4 col-lg-4 col-xl-3" key={"linegroup-checkbox-w-" + line_group.uuid}>
                <Checkbox
                    label={line_group.title}
                    isSelected={this.state.selected_line_group_uuids[line_group.uuid]}
                    onCheckboxChange={this.handleLinegroupChange}
                    name={"linegroup-checkbox-" + line_group.uuid}
                    key={"linegroup-checkbox-" + line_group.uuid}
                />
            </div>
        ));
        if (this.state.insights_show_checkboxes) {
            const totalCounter = Object.keys(this.state.selected_line_group_uuids).length;
            const selectedCounter = this.countSelectedCheckboxes(this.state.selected_line_group_uuids);
            const selectAllActive = totalCounter && selectedCounter === totalCounter;
            const deselectAllActive = totalCounter && selectedCounter === 0;
            return (
                <div className="filter-group">
                    <div className="filter-group-header">
                        <h6>
                            <FormattedMessage id="common.linegroups" defaultMessage="Line-groups" />
                        </h6>
                        <div className="filter-group-actions ml-auto">
                            <a
                                className={selectAllActive ? "is-active" : undefined}
                                onClick={() => {
                                    this.setAllLinegroupsFilter(true);
                                }}
                            >
                                <FormattedMessage id="common.select_all" defaultMessage="Select all" />
                            </a>
                            <a
                                className={deselectAllActive ? "is-active" : undefined}
                                onClick={() => {
                                    this.setAllLinegroupsFilter(false);
                                }}
                            >
                                <FormattedMessage id="common.deselect_all" defaultMessage="Deselect all" />
                            </a>
                        </div>
                    </div>
                    <div className="row">{checkboxes}</div>
                </div>
            );
        }
    }

    renderLineCheckboxes(lines: ILocalLine[]) {
        const checkboxes = lines.map(line => (
            <div className="col-sm-4 col-lg-4 col-xl-3" key={"line-checkbox-w-" + line.uuid}>
                <Checkbox
                    label={line.title}
                    isSelected={this.state.selected_line_uuids[line.uuid]}
                    onCheckboxChange={this.handleLineChange}
                    name={"line-checkbox-" + line.uuid}
                    key={"line-checkbox-" + line.uuid}
                />
            </div>
        ));
        if (this.state.insights_show_checkboxes) {
            const totalCounter = Object.keys(this.state.selected_line_uuids).length;
            const selectedCounter = this.countSelectedCheckboxes(this.state.selected_line_uuids);
            const selectAllActive = totalCounter && selectedCounter === totalCounter;
            const deselectAllActive = totalCounter && selectedCounter === 0;
            return (
                <div className="filter-group">
                    <div className="filter-group-header">
                        <h6>
                            <FormattedMessage id="common.lines" defaultMessage="Lines" />
                        </h6>
                        <div className="filter-group-actions ml-auto">
                            <a
                                className={selectAllActive ? "is-active" : undefined}
                                onClick={() => {
                                    this.setAllLinesFilter(true);
                                }}
                            >
                                <FormattedMessage id="common.select_all" defaultMessage="Select all" />
                            </a>
                            <a
                                className={deselectAllActive ? "is-active" : undefined}
                                onClick={() => {
                                    this.setAllLinesFilter(false);
                                }}
                            >
                                <FormattedMessage id="common.deselect_all" defaultMessage="Deselect all" />
                            </a>
                        </div>
                    </div>
                    <div className="row">{checkboxes}</div>
                </div>
            );
        }
    }

    renderInsightTypeCheckboxes(insightTypes: ILocalInsightType[]) {
        const checkboxes = insightTypes.map(insight => (
            <div className="col-sm-4 col-lg-4 col-xl-3" key={"type-checkbox-w-" + insight.type}>
                <Checkbox
                    label={insight.name}
                    isSelected={this.state.selected_insight_types[insight.type]}
                    onCheckboxChange={this.handleTypeChange}
                    name={"type-checkbox-" + insight.type}
                    key={"type-checkbox-" + insight.type}
                />
            </div>
        ));
        if (this.state.insights_show_checkboxes) {
            const totalCounter = Object.keys(this.state.selected_insight_types).length;
            const selectedCounter = this.countSelectedCheckboxes(this.state.selected_insight_types);
            const selectAllActive = totalCounter && selectedCounter === totalCounter;
            const deselectAllActive = totalCounter && selectedCounter === 0;
            return (
                <div className="filter-group">
                    <div className="filter-group-header">
                        <h6>
                            <FormattedMessage id="common.insight_types" defaultMessage="Insight types" />
                        </h6>
                        <div className="filter-group-actions ml-auto">
                            <a
                                className={selectAllActive ? "is-active" : undefined}
                                onClick={() => {
                                    this.setAllTypesFilter(true);
                                }}
                            >
                                <FormattedMessage id="common.select_all" defaultMessage="Select all" />
                            </a>
                            <a
                                className={deselectAllActive ? "is-active" : undefined}
                                onClick={() => {
                                    this.setAllTypesFilter(false);
                                }}
                            >
                                <FormattedMessage id="common.deselect_all" defaultMessage="Deselect all" />
                            </a>
                        </div>
                    </div>
                    <div className="row">{checkboxes}</div>
                </div>
            );
        }
    }

    countSelectedCheckboxes(checkboxesMap: IUuidSelected) {
        let selectedCounter = 0;
        Object.entries(checkboxesMap).forEach(([, value]) => {
            if (value) {
                selectedCounter = selectedCounter + 1;
            }
        });
        return selectedCounter;
    }

    checkBoxesSelectedOptionsMessage() {
        let totalCounter =
            Object.keys(this.state.selected_insight_types).length + Object.keys(this.state.selected_line_uuids).length;
        let selectedCounter =
            this.countSelectedCheckboxes(this.state.selected_insight_types) +
            this.countSelectedCheckboxes(this.state.selected_line_uuids);
        if (selectedCounter === totalCounter) {
            return "common.filters_selected_all";
        }
        if (selectedCounter === 0) {
            return "common.filters_selected_none";
        }
        return "common.filters_selected_some";
    }

    exportHTML = (num_days: number) => {
        exportInsightsHTML(
            renderToString(<IntlProviderWrapper>{this.renderInsights(true, num_days)}</IntlProviderWrapper>)
        );
    };

    handleExportXlsx = (event: Event) => {
        const data = prepareInsightsExportTable(this.props.insights);
        let sheet_name = translate("common.insights", "Insights");
        let file_name = "insights.xlsx";

        if (event instanceof CustomEvent) {
            if (event.detail === INSIGHT_TYPES.tool_setup) {
                sheet_name = translate("Manufacturing.ToolsView.tool_change", "Tool changes");
                file_name = "tool-changes.xlsx";
            }
        }

        const wb = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        XLSX.utils.book_append_sheet(wb, worksheet, sheet_name);
        XLSX.writeFile(wb, file_name);
    };

    handleExportHTML = () => {
        this.setState({
            show_print_modal: true,
        });
    };

    handlePrintModalPrint = (state: PrintModalState) => {
        this.setState({
            show_print_modal: false,
        });
        this.exportHTML(state.num_days);
    };

    handlePrintModalHide = () => {
        this.setState({
            show_print_modal: false,
        });
    };

    renderInsights(is_export: boolean, num_days: number = 0) {
        let selected_insight_types = this.state.selected_insight_types;

        if (is_export) {
            for (const el in selected_insight_types) {
                selected_insight_types[el] = false;
            }

            selected_insight_types.tool_setup = true;
        }

        if (this.props.insights.length === 0) {
            return (
                <ErrorComponent
                    msg={translate("common.no_insights_available", "No insights available")}
                    type="warning"
                />
            );
        }

        return (
            <div className="table insight__table">
                <div className="insight__table-head">
                    <dl className="insight__table-row">
                        <dd className="font-size-14 insight__table-cell">{translate("common.shift", "Shift")}</dd>
                        <dd className="font-size-14 insight__table-cell">{translate("common.line", "Line")}</dd>
                        <dd className="font-size-14 insight__table-cell">{translate("common.insight", "Insight")}</dd>
                    </dl>
                </div>
                <div className="insight__table-body">
                    <Insights
                        insights={this.props.insights}
                        selected_line_uuids={this.state.selected_line_uuids}
                        selected_insight_types={selected_insight_types}
                        show_only_future={this.props.show_only_future}
                        history={this.props.history}
                        show_links={this.props.show_links}
                        reportid={this.props.reportid}
                        showActions={this.props.show_actions}
                        reverse_order={this.props.reverse_order}
                        is_export={is_export}
                        num_days={num_days}
                        allowed_actions={this.props.allowed_actions}
                        onActionSelect={this.props.onActionSelect}
                    />
                </div>
            </div>
        );
    }

    render() {
        return (
            <div ref={this.nodeRef} id="insights-list" className="insights-list">
                {this.props.show_filter && (
                    <a
                        className={
                            "card-header bg-transparent d-flex align-items-center" +
                            (this.state.insights_show_checkboxes ? " is-expanded" : "")
                        }
                        onClick={this.toggleFilters}
                    >
                        {!this.state.insights_show_checkboxes && (
                            <FormattedMessage id="common.show_filters" defaultMessage="Show filters" />
                        )}
                        {this.state.insights_show_checkboxes && (
                            <FormattedMessage id="common.hide_filters" defaultMessage="Hide filters" />
                        )}
                        &nbsp;
                        <span className="text-muted">
                            (<FormattedMessage id={this.checkBoxesSelectedOptionsMessage()} defaultMessage="" />)
                        </span>
                    </a>
                )}
                <div className="card-body">
                    {this.props.show_filter && this.renderPlantCheckboxes(this.state.plants)}
                    {this.props.show_filter && this.renderLinegroupCheckboxes(this.state.line_groups)}
                    {this.props.show_filter && this.renderInsightTypeCheckboxes(this.state.insight_types)}
                    {this.state.loading ? <Loader /> : this.renderInsights(false)}
                </div>
                <PrintModal
                    show={this.state.show_print_modal}
                    onPrint={this.handlePrintModalPrint}
                    onHide={this.handlePrintModalHide}
                />
            </div>
        );
    }
}

export default InsightsList;
